/* Copyright (c)2023 Sky Co., LTD. All rights reserved. */
import * as schema from '@/models/Mobile/Android/AndroidManagementV1Schema'
import { cloneDeep } from 'lodash';

export namespace ApplicationPolicyDef {

    // 配布設定
    export const DISTRIBUTION_TYPE = {
        Black: 1,       // 配布禁止（禁止アプリケーション）
        WhiteManual: 2, // 手動配布
        WhiteAuto: 3,   // 自動配布
        WhiteForce: 4,  // 自動配布（アンインストール禁止）
    } as const;
    export type DISTRIBUTION_TYPE = typeof DISTRIBUTION_TYPE[keyof typeof DISTRIBUTION_TYPE];

    // 更新設定
    export const UPDATE_TYPE = {
        Default: "AUTO_UPDATE_DEFAULT",       // 通常更新
        Postponed: "AUTO_UPDATE_POSTPONED",     // 遅延更新
        HighPriority: "AUTO_UPDATE_HIGH_PRIORITY"   // 即時更新
    } as const;
    export type UPDATE_TYPE = typeof UPDATE_TYPE[keyof typeof UPDATE_TYPE];

    // アクセス権限
    export const PERMISSION_GRANT_TYPE = {
        Prompt: "PROMPT", // ユーザーが許可
        Grant: "GRANT",   // 自動的に許可
        Deny: "DENY"   // 許可しない
    } as const;
    export type PERMISSION_GRANT_TYPE = typeof PERMISSION_GRANT_TYPE[keyof typeof PERMISSION_GRANT_TYPE];
}


/*
 * AMAPI_ApplicationPolicyの解析などのユーティリティを提供します
 */
export class ApplicationPolicyConverter {

    /*
     * パッケージ名の取得
     */
    public GetPackageName(policy: schema.AMAPI_ApplicationPolicyExtend): string {
        if (null == policy.packageName || undefined == policy.packageName) {
            return "";
        }
        return policy.packageName as string;
    }

    // #region ----------- 配布設定 -----------

    private readonly DISTRIBUTION_TYPE_WHITEMANUAL_JSON = "AVAILABLE";
    private readonly DISTRIBUTION_TYPE_WHITEAUTO_JSON = "PREINSTALLED";
    private readonly DISTRIBUTION_TYPE_WHITEFORCE_JSON = "FORCE_INSTALLED";
    private readonly DISTRIBUTION_TYPE_BLACK_JSON = "BLOCKED";

    /*
     * 配布設定の種類を取得
     * @return ApplicationPolicyDef.DISTRIBUTION_TYPE
     */
    public GetDistributionType(policy: schema.AMAPI_ApplicationPolicyExtend): ApplicationPolicyDef.DISTRIBUTION_TYPE {
        if (null == policy.installType || undefined == policy.installType) {
            // installTypeがないことはないと思うが、厳しい方（禁止）に倒す
            return ApplicationPolicyDef.DISTRIBUTION_TYPE.Black;
        }

        const upper = policy.installType.toUpperCase();
        if (this.DISTRIBUTION_TYPE_WHITEMANUAL_JSON === upper) {
            return ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteManual;
        }
        else if (this.DISTRIBUTION_TYPE_WHITEAUTO_JSON === upper) {
            // 配布許可
            return ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteAuto;
        }
        else if (this.DISTRIBUTION_TYPE_WHITEFORCE_JSON === upper) {
            // 配布許可（アンインストール禁止）
            return ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteForce;
        }
        else if (this.DISTRIBUTION_TYPE_BLACK_JSON === upper) {
            // 配布禁止（禁止アプリケーション）
            return ApplicationPolicyDef.DISTRIBUTION_TYPE.Black;
        }
        else {
            // 対象外のインストール方法は、厳しい方（禁止）に倒す
            return ApplicationPolicyDef.DISTRIBUTION_TYPE.Black;
        }
    }

    /*
     * 配布設定の種類を設定
     * @param[out] policy : 設定先
     * @param[in]  type : 設定内容
     */
    public SetDistributionType(policy: schema.AMAPI_ApplicationPolicyExtend, type: ApplicationPolicyDef.DISTRIBUTION_TYPE): void {

        switch (type) {
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteManual:
                policy.installType = this.DISTRIBUTION_TYPE_WHITEMANUAL_JSON;
                break;
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteAuto:
                policy.installType = this.DISTRIBUTION_TYPE_WHITEAUTO_JSON;
                break;
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteForce:
                policy.installType = this.DISTRIBUTION_TYPE_WHITEFORCE_JSON;
                break;
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.Black:
                policy.installType = this.DISTRIBUTION_TYPE_BLACK_JSON;
                break;
            default:
                break;
        }
    }

    /*
     * 配布設定の文言を取得
     */
    public GetDistributionText(policy: schema.AMAPI_ApplicationPolicyExtend): string {
        switch (this.GetDistributionType(policy)) {
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteManual:
                return "任意インストール";
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteAuto:
                return "プレインストール";
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.WhiteForce:
                return "強制インストール";
            case ApplicationPolicyDef.DISTRIBUTION_TYPE.Black:
                // ブラックリストが文言表示されることはないので空白
                return "";
            default:
                return "";
        }
    }

    // #endregion -----------配布設定-----------

    // #region ----------- 更新設定 -----------

    /*
     * 更新設定の種類を取得
     * @return ApplicationPolicyDef.UPDATE_TYPE
     */
    public GetUpdateType(policy: schema.AMAPI_ApplicationPolicyExtend): ApplicationPolicyDef.UPDATE_TYPE {
        if (null == policy.autoUpdateMode || undefined == policy.autoUpdateMode) {
            // autoUpdateModeがないことはないと思うが、デフォルトを返す
            return ApplicationPolicyDef.UPDATE_TYPE.Default;
        }

        const upper = policy.autoUpdateMode.toUpperCase();
        if (
            ApplicationPolicyDef.UPDATE_TYPE.Default === upper      // 通常更新
            || ApplicationPolicyDef.UPDATE_TYPE.Postponed === upper // 遅延更新
            || ApplicationPolicyDef.UPDATE_TYPE.HighPriority === upper  // 即時更新
        ) {
            return upper;
        }
        else {
            // 対象外はデフォルトを返す
            return ApplicationPolicyDef.UPDATE_TYPE.Default;
        }
    }

    /*
     * 更新設定の種類を設定
     * @param[out] policy : 設定先
     * @param[in]  type : 設定内容
     */
    public SetUpdateType(policy: schema.AMAPI_ApplicationPolicyExtend, type: ApplicationPolicyDef.UPDATE_TYPE): void {

        switch (type) {
            case ApplicationPolicyDef.UPDATE_TYPE.Default:
            case ApplicationPolicyDef.UPDATE_TYPE.Postponed:
            case ApplicationPolicyDef.UPDATE_TYPE.HighPriority:
                policy.autoUpdateMode = type;
                break;
            default:
                // 対象外はデフォルトを入れる
                policy.autoUpdateMode = ApplicationPolicyDef.UPDATE_TYPE.Default;
                break;
        }
    }

    /*
     * 更新設定のテキストを取得
     */
    public GetUpdateText(policy: schema.AMAPI_ApplicationPolicyExtend): string {
        switch (this.GetUpdateType(policy)) {
            case ApplicationPolicyDef.UPDATE_TYPE.Default:
                return "標準アップデート";
            case ApplicationPolicyDef.UPDATE_TYPE.Postponed:
                return "遅延アップデート";
            case ApplicationPolicyDef.UPDATE_TYPE.HighPriority:
                return "即時アップデート";
            default:
                return "";
        }
    }

    // #endregion -----------更新設定-----------

    // #region ----------- 一時利用停止 -----------

    /*
     * 一時利用停止の判定を取得
     * @return true : 一時利用停止
     */
    public GetDisabled(policy: schema.AMAPI_ApplicationPolicyExtend): boolean {
        if (null == policy.disabled || undefined == policy.disabled) {
            // disabledがないとき（実動作で確認）
            return false;
        }

        return policy.disabled;
    }

    /*
     * 一時利用停止の判定を設定
     * @param[out] policy : 設定先
     * @param[in]  type : 設定内容
     */
    public SetDisabled(policy: schema.AMAPI_ApplicationPolicyExtend, disabled: boolean): void {
        policy.disabled = disabled;
    }

    /*
     * 更新設定のテキストを取得
     */
    public GetDisabledText(policy: schema.AMAPI_ApplicationPolicyExtend): string {
        if (this.GetDisabled(policy)) {
            return "利用停止中";
        }
        else {
            return "利用可";
        }
    }

    // #endregion ----------- 一時利用停止 -----------

    // #region ----------- アクセス権限 -----------

    /*
     * アクセス権限の一覧情報からデータを作成します
     * @param[in] policyApp : アクセス権限の一覧情報
     * @param[in] margeData : マージ元のデータ（undefinedならマージ不要(デフォルト値)）
     * @return 結果(Array<schema.AMAPI_PermissionGrant>)
     * @note
     *  マージの動作に付いて
     *      policyApp   margeData
     *      有り        無し        ：初期値（ユーザーが許可）
     *      有り        有り        ：以前のデータを引き継ぐ（margeData）
     *      無し        有り        ：削除
     */
    public CreatePermissionSetting(policyApp: Array<schema.AMAPI_ApplicationPermission>, margeData: Array<schema.AMAPI_PermissionGrant> | undefined): { [key: string]: ApplicationPolicyDef.PERMISSION_GRANT_TYPE; } {
        const setting: { [key: string]: any; } = {};

        // マージデータに簡単にアクセスできるようにmap化
        const map = new Map<string, schema.AMAPI_PermissionGrant>();
        if (undefined != margeData) {
            margeData.forEach(x => {
                if (null != x.permission && undefined != x.permission) {
                    // IDがnullやundefinedはありえない
                    map.set(x.permission, x);
                }
            });
        }

        policyApp.forEach(x => {
            if (null == x.permissionId || undefined == x.permissionId) {
                // IDがないアクセス権限は想定外
                return;
            }

            const item = new schema.AMAPI_PermissionGrant();
            item.permission = x.permissionId;

            const find = map.get(x.permissionId);
            if (undefined === find) {
                // マージ元データがないのでデフォルト値
                setting[x.permissionId] = ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt;
            }
            else {
                // マージデータ発見したので引き継ぐ
                if (null == find.policy || undefined == find.policy) {
                    // 前回データが空ならデフォルト値
                    setting[x.permissionId] = ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt;
                }
                else {
                    setting[x.permissionId] = find.policy;
                }
            }
        });

        return setting;
    }

    /*
     * アクセス権限一覧の文言を取得
     */
    public GetPermissionSettingListText(defaultPermission: ApplicationPolicyDef.PERMISSION_GRANT_TYPE, permissionGrants: Array<schema.AMAPI_PermissionGrant> | undefined): string {
        if (undefined == permissionGrants || 0 == permissionGrants.length) {
            return this.GetPermissionSettingText(defaultPermission);
        }

        return this.GetPermissionSettingText(defaultPermission) + "（個別設定あり）";
    }

    /*
     * アクセス権限名称を取得
     * @param[in] managedConfiguration : 管理設定の設定一覧
     */
    public GetPermissionNameText(policyApp: schema.AMAPI_ApplicationPermission): string {
        if (null == policyApp.name || undefined == policyApp.name || '' == policyApp.name) {
            // タイトルがないときはIDを返す
            if (null == policyApp.permissionId || undefined == policyApp.permissionId) {
                return "";
            }
            return policyApp.permissionId;
        }
        return policyApp.name;
    }

    /*
     * 文字列からPERMISSION_GRANT_TYPEへ変換する
     */
    public ConvertStringToPermissionType(type: string | null | undefined): ApplicationPolicyDef.PERMISSION_GRANT_TYPE {
        if (undefined == type || null == type) {
            return ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt;
        }
        if (type == ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt) {
            return ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt;
        }
        else if (type == ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Grant) {
            return ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Grant;
        }
        else if (type == ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Deny) {
            return ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Deny;
        }
        return ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt;
    }

    /*
     * 許可設定の文言を取得
     * @param[in] permissionGrants : アクセス権限の設定一覧）
     */
    public GetPermissionSettingText(type: ApplicationPolicyDef.PERMISSION_GRANT_TYPE): string {
        switch (type) {
            case ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Prompt:
                return "端末利用者が設定";
            case ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Grant:
                return "自動的に許可";
            case ApplicationPolicyDef.PERMISSION_GRANT_TYPE.Deny:
                return "許可しない";
        }
        return "";
    }

    // #endregion ----------- アクセス権限 -----------

    // #region ----------- 管理設定 -----------

    /*
     * 管理設定の一覧情報からデータを作成します
     * @param[in] policyApp : 管理設定の一覧情報
     * @param[in] margeData : マージ元のデータ（undefined, nullならマージ不要(デフォルト値)）
     * @return 結果 連想配列({ [key: string]: any; })
     * @note
     *  マージの動作に付いて
     *      policyApp   margeData
     *      有り        無し        ：初期値（ユーザーが許可）
     *      有り        有り        ：以前のデータを引き継ぐ（margeData）
     *      無し        有り        ：削除
     */
    public CreateMgrConfiguration(policyApp: Array<schema.AMAPI_ManagedProperty>, margeData: { [key: string]: any; } | null | undefined): { [key: string]: any; } {
        const setting: { [key: string]: any; } = {};

        // マージデータに簡単にアクセスできるようにmap化
        const map = new Map<string, any>();
        if (undefined != margeData || null != margeData) {
            for (const key in margeData) {
                map.set(key, margeData[key]);
            }
        }

        policyApp.forEach(x => {
            if (null == x.key || undefined == x.key) {
                // keyがないアクセス権限は想定外
                return;
            }

            const find = map.get(x.key);
            if (undefined === find) {
                // マージ元データがないのでデフォルト値
                setting[x.key] = cloneDeep(this.GetDefaultMgrConfiguration(x));
            }
            else {
                setting[x.key] = cloneDeep(find);
            }
        });
        
        return setting;
    }

    /*
     * 管理設定のデフォルト値を返します
     * @param[in] type : 種類(STRINGとかBOOLとか)
     * @param[in] defaultValue : デフォルト値（指定があれば）
     * @return any
     * @note
     *  BOOL : Adobe
     *  STRING : Googleなど
     *  
     */
    private GetDefaultMgrConfiguration(managedProperty: schema.AMAPI_ManagedProperty): any {
        if (null != managedProperty.defaultValue && undefined != managedProperty.defaultValue) {
            // デフォルトの指定があるので、それをそのまま返す
            return managedProperty.defaultValue;
        }

        // デフォルトの指定がないので、タイプに合わせてデフォルト値を返す
        if (null == managedProperty.type || undefined == managedProperty.type) {
            return null;
        }

        if ("BOOL" == managedProperty.type) {
            return false;
        }
        else if ("STRING" == managedProperty.type) {
            return "";
        }
        else if ("INTEGER" == managedProperty.type) {
            return 0;
        }
        else if ("CHOICE" == managedProperty.type) {
            // デフォルトがなければ、選択肢の一番上のやつ
            if (undefined != managedProperty.entries && 0 != managedProperty.entries.length) {
                return managedProperty.entries[0].value;
            }
            return "";
        }
        else if ("MULTISELECT" == managedProperty.type) {
            return new Array<string>();
        }
        else if ("HIDDEN" == managedProperty.type) {
            return "";
        }
        else if ("BUNDLE_ARRAY" == managedProperty.type) {
            return new Array<any>();
        }
        else if ("BUNDLE" == managedProperty.type) {
            return null;
        }
        return null;
    }

    /*
     * 管理設定の文言を取得
     * @param[in] managedConfiguration : 管理設定の設定一覧
     */
    public GetMgrConfigurationText(managedConfiguration: { [key: string]: any;} | null | undefined): string {
        if (null != managedConfiguration && undefined != managedConfiguration) {
            for (const key in managedConfiguration) {
                // 1ループでもすれば設定あり
                return "設定あり";
            }
        }
        return "設定なし";
    }

    // #endregion ----------- 管理設定 -----------
}

/* Copyright (c)2023 Sky Co., LTD. All rights reserved. */
